import React, {useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledTasksDashboardCard from 'components/organisms/Cards/TasksDashboardCard/TasksDashboardCard.styles';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';

import ActionSwitch from 'components/molecules/Switches/ActionSwitch/ActionSwitch';
import TasksList from 'components/organisms/Lists/TasksList/TasksList';
import {useCommentToggleTaskDone} from 'services/comment/comment.utils';
import {useCommentList} from 'services/comment/comment.hooks';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';

const TasksDashboardCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'TasksDashboardCard', {
    children: ['tasks']
  });

  const innerRef = useRef(null);
  const authorize = useAuthorize();
  const profileProvider = useProfile();
  const cardState = profileProvider.cardState;

  useImperativeHandle(ref, () => innerRef.current);

  const showMyTasks = Boolean(cardState.cardSettings['tasks']?.filter?.showMyTasks);
  const showDueTasks = Boolean(cardState.cardSettings['tasks']?.filter?.showDueTasks);
  const tasks = useCommentList({
    page: 0,
    pageSize: 10,
    filter: (showMyTasks ? [{id: 'responsible', value: true}] : [])
      .concat(showDueTasks ? [{id: 'due', value: true}] : [])
      .concat([{id: 'open', value: true}, {id: 'task', value: true}]),
    sort: showDueTasks ? [{id: 'dueDate', desc: true}] : [{id: 'createdAt', desc: true}]
  }, {
    ...constants.queryOptions.infinite
  });
  const isLoading = tasks.status.isLoading || !utils.isDefined(tasks.data);

  const setCardSettingsEvent = useEffectEvent(cardState.setCardSettings);
  const switchActions = useMemo(() => {
    return [
      {
        label: 'My tasks',
        active: showMyTasks,
        onClick: (e) => {
          setCardSettingsEvent?.((current) => ({
            ...current,
            'tasks': {
              ...current?.tasks,
              filter: {
                ...current?.tasks?.filter,
                showMyTasks: !showMyTasks
              }
            }
          }));
          e.preventDefault();
        }
      },
      {
        label: 'Due tasks',
        active: showDueTasks,
        onClick: (e) => {
          setCardSettingsEvent?.((current) => ({
            ...current,
            'tasks': {
              ...current?.tasks,
              filter: {
                ...current?.tasks?.filter,
                showDueTasks: !showDueTasks
              }
            }
          }));
          e.preventDefault();
        }
      }
    ];
  }, [setCardSettingsEvent, showMyTasks, showDueTasks]);

  useEffect(() => {
    const scroll = () => {
      return innerRef.current?.querySelector('.DataList-list')?.scrollTo({top: 0, left: 0});
    }

    return utils.observeRetry(scroll, 5);
  }, [showMyTasks, showDueTasks]);

  const renderActionSwitches = () => {
    return <React.Fragment>
      {switchActions.map((action, idx) => {
        return <ActionSwitch key={idx} action={action}
          SwitchProps={{
            size: 'medium',
            color: 'primary'
          }}/>
      })}
    </React.Fragment>
  }

  const toggleDone = useCommentToggleTaskDone();
  const taskCardProps = useMemo(() => ({
    onToggle: (task, done) => {
      return toggleDone?.(task, done);
    },
    onCanUpdate: (task, markDone) => {
      return authorize({
        attribute: markDone ? 'task.markDone' : 'task.update',
        meta: {task}
      });
    },
    orientation: 'horizontal',
    size: 'small'
  }), [authorize, toggleDone]);

  const handleClick = (e, task) => {
    if (!e.defaultPrevented) {
      profileProvider.openTask(task.commentId);
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledTasksDashboardCard ref={innerRef} {...innerProps}
                                   title={'Tasks'}
                                   context={renderActionSwitches()}>
    {(tasks.data?.length > 0 || isLoading) ?
      <TasksList className="TasksDashboardCard-tasks"
                 tasks={tasks}
                 onClick={handleClick}
                 TaskCardProps={taskCardProps} /> : null}

    {(tasks.data?.length === 0 && !isLoading) ?
      <Box className="DashboardCard-empty">
        <P>No task(s) found</P>
      </Box> : null}
  </StyledTasksDashboardCard>
});

TasksDashboardCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
};

export default TasksDashboardCard;
